import { Button } from "@shared/ui";
import { stubAccount, updateIgnition } from "../lib/axle";
import { Field, InlineError, inputClassName } from "../components/Field";
import StepBody from "../components/StepBody";
import StepFooter from "../components/StepFooter";
import StepHeading from "../components/StepHeading";
import CarrierMark from "../components/CarrierMark";

// External dependencies
import { get, isEmpty, trim } from "lodash";
import { useEffect, useState } from "react";
import { handleStubAccountError } from "../lib/errorHandlersUtility";

// Validation messages shown under their field rather than in the error box.
const FIELD_ERRORS = {
  "manual-carrier": ["Enter your carrier"],
  "manual-first-name": ["Enter the policyholder’s first name"],
  "manual-last-name": ["Enter the policyholder’s last name"],
};

const validateAccountInput = (
  hasCarrier,
  carrierName,
  firstName,
  lastName,
  setError
) => {
  carrierName = trim(carrierName);
  firstName = trim(firstName);
  lastName = trim(lastName);

  if (!hasCarrier && !carrierName) {
    setError(FIELD_ERRORS["manual-carrier"][0]);
    return false;
  }

  if (!firstName) {
    setError(FIELD_ERRORS["manual-first-name"][0]);
    return false;
  }

  if (!lastName) {
    setError(FIELD_ERRORS["manual-last-name"][0]);
    return false;
  }

  return true;
};

const ManualAccount = ({
  step,
  nextStep,
  accountInfo,
  setAccountInfo,
  loginInformation,
  setLoginInformation,
  session,
  history,
  setHistory,
  posthog,
}) => {
  useEffect(() => {
    posthog.capture("$pageview", { step });
  }, [posthog]);

  const ignitionToken = session.id;

  const [carrierName, setCarrierName] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [error, setError] = useState(null);

  useEffect(() => {
    if (get(loginInformation, "carrier.id")) {
      async function updateIgnitionWithCarrier() {
        const params = {
          carrier: get(loginInformation, "carrier.id"),
        };
        const [updateIgnitionResponse, updateIgnitionError] =
          await updateIgnition(session.id, params);

        if (updateIgnitionError) {
          switch (get(updateIgnitionError, "code")) {
            case 500:
              console.log(JSON.stringify(updateIgnitionError));
              nextStep("axle-error");
              return;
            default:
              nextStep("connection-error");
              return;
          }
        }
      }
      updateIgnitionWithCarrier();
    }

    const isManualEnabled = get(session, "config.manual.enabled", false);

    if (!isManualEnabled) {
      nextStep("exit");
      return;
    }
    const isEnhancedEnabled = get(session, "config.manual.enhanced");

    if (isEnhancedEnabled) {
      nextStep("enhanced-manual-policy-document");

      setHistory(history);

      return;
    }
  }, []);

  const hasCarrier = get(loginInformation, "carrier.id") ? true : false;

  const onSubmit = async (e) => {
    e.preventDefault();

    if (
      !validateAccountInput(
        hasCarrier,
        carrierName,
        firstName,
        lastName,
        setError
      )
    ) {
      return;
    }

    const processDetail = "policy-form";

    console.log("Account doesn't exist, creating account...");

    //TODO: remove once we validate which pathway doesn't set ignitionToken (refer to AXL-4235)
    if (isEmpty(ignitionToken)) {
      console.log(`processDetail: ${processDetail}`);
      console.error("No ignitionToken provided"); //Use this .error to search in Sentry
    }

    // Save account information
    const [stubAccountResponse, stubAccountResponseError] = await stubAccount(
      ignitionToken,
      {
        firstName: firstName,
        lastName: lastName,
        carrier: hasCarrier ? get(loginInformation, "carrier.id") : carrierName,
      },
      processDetail
    );

    if (stubAccountResponseError) {
      handleStubAccountError(stubAccountResponseError, nextStep, setError);
      return;
    }

    // Save policy information
    setAccountInfo({
      id: get(stubAccountResponse, "id", null),
      firstName,
      lastName,
      carrier: hasCarrier ? get(loginInformation, "carrier.id") : carrierName,
    });

    // Set result to manual if not basic
    loginInformation.result === "basic"
      ? setLoginInformation({
          ...loginInformation,
          result: "basic",
          resultDetail: "basic",
        })
      : setLoginInformation({
          ...loginInformation,
          result: "manual",
          resultDetail: "policy-form",
        });

    nextStep("manual-policy-info");
  };

  const fieldError = Object.keys(FIELD_ERRORS).find((id) =>
    FIELD_ERRORS[id].includes(error)
  );

  return (
    <form className="flex min-h-0 flex-1 flex-col" onSubmit={onSubmit}>
      <StepBody>
        <CarrierMark loginInformation={loginInformation} />
        <StepHeading
          title="Enter your policy holder's information"
          description={
            hasCarrier ? (
              <>
                Axle will provide this information if we’re unable to connect with{" "}
                <span className="font-medium text-ink">
                  {loginInformation.carrier.name}
                </span>
                .
              </>
            ) : (
              <>Axle will use this information to verify your policy details.</>
            )
          }
        />
        <div className="flex flex-col gap-4">
          {error && !fieldError && (
            <div
              className="rounded border border-danger-border p-3"
              role="status"
            >
              <InlineError>{error}</InlineError>
            </div>
          )}
          {!hasCarrier && (
            <Field
              id="manual-carrier"
              label="Carrier"
              error={fieldError === "manual-carrier" ? error : undefined}
            >
              <input
                id="manual-carrier"
                aria-invalid={fieldError === "manual-carrier"}
                aria-describedby={fieldError === "manual-carrier" ? "manual-carrier-error" : undefined}
                placeholder="e.g. State Farm"
                value={carrierName}
                className={inputClassName}
                type="text"
                onChange={(e) => setCarrierName(e.target.value)}
              />
            </Field>
          )}
          <Field
            id="manual-first-name"
            label="First name"
            error={fieldError === "manual-first-name" ? error : undefined}
          >
            <input
              id="manual-first-name"
              aria-invalid={fieldError === "manual-first-name"}
              aria-describedby={fieldError === "manual-first-name" ? "manual-first-name-error" : undefined}
              placeholder="e.g. Jane"
              value={firstName}
              className={inputClassName}
              type="text"
              onChange={(e) => setFirstName(e.target.value)}
            />
          </Field>
          <Field
            id="manual-last-name"
            label="Last name"
            error={fieldError === "manual-last-name" ? error : undefined}
          >
            <input
              id="manual-last-name"
              aria-invalid={fieldError === "manual-last-name"}
              aria-describedby={fieldError === "manual-last-name" ? "manual-last-name-error" : undefined}
              placeholder="e.g. Smith"
              value={lastName}
              className={inputClassName}
              type="text"
              onChange={(e) => setLastName(e.target.value)}
            />
          </Field>
        </div>
      </StepBody>
      <StepFooter>
        <Button type={"submit"}>Continue</Button>
      </StepFooter>
    </form>
  );
};

export default ManualAccount;
